import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ICON = {
  green: <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />,
  amber: <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" />,
  red: <XCircle className="w-5 h-5 text-rose-400 shrink-0" />,
};

const ItemLink = ({ i, onPurge }) => {
  if (!i.link || i.ok) return null;
  if (i.link === "purge") return <button data-testid={`launch-purge-${i.key}`} onClick={onPurge} className="btn-ghost px-3 py-1.5 text-xs">Remove test accounts</button>;
  return (
    <a data-testid={`launch-link-${i.key}`} href={i.link} target="_blank" rel="noreferrer" className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1">
      Open <ExternalLink className="w-3 h-3" />
    </a>
  );
};

const Item = ({ i, onToggle, onPurge }) => (
  <div data-testid={`launch-item-${i.key}`} data-status={i.status} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
    <div className="flex items-start gap-3 min-w-0">
      {ICON[i.status]}
      <div className="min-w-0">
        <p className="text-sm">{i.label}{i.severity === "blocker" && !i.ok && <span className="ml-2 text-[10px] uppercase font-mono-lux text-rose-300">blocks launch</span>}</p>
        <p data-testid={`launch-fix-${i.key}`} className="text-xs text-slate-500 mt-1">{i.fix}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0 pl-8 sm:pl-0">
      <ItemLink i={i} onPurge={onPurge} />
      {i.auto
        ? <span className="text-[10px] font-mono-lux uppercase text-slate-500">auto</span>
        : <button data-testid={`launch-toggle-${i.key}`} onClick={() => onToggle(i)} className="btn-ghost px-3 py-1.5 text-xs">{i.ok ? "Mark not done" : "Mark done"}</button>}
    </div>
  </div>
);

const Summary = ({ d }) => (
  <div data-testid="launch-summary" className={`lux-card p-6 border ${d.ready ? "border-emerald-400/40" : "border-rose-400/40"}`}>
    <p className="eyebrow flex items-center gap-2"><Rocket className="w-4 h-4" />Launch readiness</p>
    <p data-testid="launch-banner" className={`text-2xl font-semibold mt-3 ${d.ready ? "text-emerald-400" : "text-rose-300"}`}>
      {d.ready ? "Ready to launch" : `${d.blockers} item${d.blockers === 1 ? "" : "s"} blocking launch`}
    </p>
    <p className="text-sm text-slate-400 mt-2">
      <span data-testid="launch-progress" className="font-mono-lux">{d.done} / {d.total}</span> done ·{" "}
      <span data-testid="launch-warnings">{d.warnings}</span> warning{d.warnings === 1 ? "" : "s"}. Red items must be fixed before taking real money; amber items are recommended.
    </p>
  </div>
);

export const LaunchChecklist = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/launch-checklist").then((r) => { setD(r.data); setErr(""); })
    .catch((e) => setErr(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = (i) => api.put(`/admin/launch-checklist/${i.key}`, { done: !i.ok }).then(load).catch((e) => toast.error(formatApiError(e)));
  const purge = async () => {
    if (!window.confirm("Remove every @example.com test account (except you) and its data?")) return;
    try { const { data } = await api.post("/admin/test-accounts/purge"); toast.success(`Removed ${data.removed} test account(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (err && !d) return <p data-testid="launch-error" className="text-rose-300 text-sm">{err} <button onClick={load} className="underline">Retry</button></p>;
  if (!d) return <p className="text-slate-500 text-sm">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="launch-checklist">
      <Summary d={d} />
      {d.sections.map((sec) => {
        const items = d.items.filter((i) => i.section === sec);
        if (!items.length) return null;
        const red = items.filter((i) => i.status === "red").length;
        return (
          <div key={sec} data-testid={`launch-section-${sec.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="eyebrow mb-2">{sec} <span className="text-slate-500 normal-case">· {items.filter((i) => i.ok).length}/{items.length}{red ? ` · ${red} blocking` : ""}</span></p>
            <div className="lux-card divide-y divide-white/5">{items.map((i) => <Item key={i.key} i={i} onToggle={toggle} onPurge={purge} />)}</div>
          </div>
        );
      })}
    </div>
  );
};
